import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Building2,
  UserX,
  Clock,
  CalendarPlus,
  AlertCircle,
  Users,
} from "lucide-react";
import type { QuickFilterCounts } from "@/types/superAdmin";

interface QuickFiltersProps {
  counts: QuickFilterCounts;
  activeFilter: string | null;
  onFilterChange: (filter: string | null) => void;
  isLoading?: boolean;
}

const filterDefs = [
  { id: "multiGym", icon: Building2, color: "bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 border-purple-500/30", activeColor: "bg-purple-500 text-white" },
  { id: "noSubscription", icon: UserX, color: "bg-orange-500/20 text-orange-300 hover:bg-orange-500/30 border-orange-500/30", activeColor: "bg-orange-500 text-white" },
  { id: "neverConnected", icon: Clock, color: "bg-red-500/20 text-red-300 hover:bg-red-500/30 border-red-500/30", activeColor: "bg-red-500 text-white" },
  { id: "inactive30d", icon: AlertCircle, color: "bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30 border-yellow-500/30", activeColor: "bg-yellow-500 text-white" },
  { id: "newThisWeek", icon: CalendarPlus, color: "bg-green-500/20 text-green-300 hover:bg-green-500/30 border-green-500/30", activeColor: "bg-green-500 text-white" },
  { id: "expiredSubscription", icon: Users, color: "bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 border-blue-500/30", activeColor: "bg-blue-500 text-white" },
];

export function QuickFilters({
  counts,
  activeFilter,
  onFilterChange,
  isLoading,
}: QuickFiltersProps) {
  const { t } = useTranslation("adminui3");
  const isMobile = useIsMobile();

  const filters = filterDefs.map((f) => ({
    ...f,
    label: t(`superAdminQuickFilters.${f.id}.label`),
    shortLabel: t(`superAdminQuickFilters.${f.id}.shortLabel`),
  }));

  return (
    <div className={cn(
      "flex flex-wrap gap-2",
      isMobile && "gap-1"
    )}>
      {filters.map((filter) => {
        const Icon = filter.icon;
        const count = counts[filter.id as keyof QuickFilterCounts] || 0;
        const isActive = activeFilter === filter.id;

        return (
          <Button
            key={filter.id}
            variant="outline"
            size={isMobile ? "sm" : "default"}
            onClick={() => onFilterChange(isActive ? null : filter.id)}
            className={cn(
              "transition-all duration-200 border",
              isActive ? filter.activeColor : filter.color,
              isMobile && "text-xs px-2 h-8"
            )}
            disabled={isLoading}
          >
            <Icon className={cn("mr-1", isMobile ? "h-3 w-3" : "h-4 w-4")} />
            <span>{isMobile ? filter.shortLabel : filter.label}</span>
            <Badge
              variant="secondary"
              className={cn(
                "ml-1",
                isMobile ? "text-[10px] px-1 py-0 h-4" : "text-xs",
                isActive && "bg-white/20"
              )}
            >
              {count}
            </Badge>
          </Button>
        );
      })}

      {activeFilter && (
        <Button
          variant="ghost"
          size={isMobile ? "sm" : "default"}
          onClick={() => onFilterChange(null)}
          className={cn(
            "text-muted-foreground hover:text-foreground",
            isMobile && "text-xs px-2 h-8"
          )}
        >
          {t("superAdminQuickFilters.clear")}
        </Button>
      )}
    </div>
  );
}
